/**
 * React Query wrapper for the exact registered-source work plan.
 *
 * The pure contract and runtime normalization live in src/lib so the server
 * build, focused tests, and browser bundle all consume the same code.
 */
import { useQuery } from "@tanstack/react-query";
import {
  normalizePolicyInboxWorkPlan,
  type PolicyInboxWorkPlanData,
} from "../../lib/policy-inbox-work-plan.js";

export {
  normalizePolicyInboxWorkPlan,
} from "../../lib/policy-inbox-work-plan.js";
export type {
  PolicyInboxRegistryState,
  PolicyInboxSourceKind,
  PolicyInboxWorkPlanAction,
  PolicyInboxWorkPlanData,
  PolicyInboxWorkPlanLatestScan,
  PolicyInboxWorkPlanRunningScan,
  PolicyInboxWorkPlanSource,
  PolicyInboxWorkPlanSummary,
  PolicyInboxWorkState,
} from "../../lib/policy-inbox-work-plan.js";

export function usePolicyInboxWorkPlan(
  workspaceId: string | null | undefined,
  includeInactive = true,
) {
  return useQuery<PolicyInboxWorkPlanData>({
    queryKey: ["policy-inbox-work-plan", workspaceId, includeInactive],
    queryFn: async () => {
      const params = new URLSearchParams({ include_inactive: String(includeInactive) });
      const res = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId!)}/policy-inbox-work-plan?${params.toString()}`,
        { credentials: "include", cache: "no-store" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.message || `Failed to load exact source plan (${res.status})`);
      }
      return normalizePolicyInboxWorkPlan(await res.json());
    },
    enabled: !!workspaceId,
    staleTime: 10_000,
    refetchInterval: (query) => query.state.data?.summary.running_source_count ? 10_000 : false,
  });
}
